import React, { useState, useEffect, useCallback } from 'react';
import { concerns as concernsApi, maintenanceRequests as mrApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { segmentName } from '../utils/segments';

const STATUS_LABELS = { open: 'Open', acknowledged: 'Acknowledged', resolved: 'Resolved' };

export default function ConcernsPage() {
  const { isAdmin } = useAuth();
  const [allConcerns, setAllConcerns] = useState([]);
  const [filter, setFilter] = useState('open');
  const [loading, setLoading] = useState(true);
  const [resolvingId, setResolvingId] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await concernsApi.list(filter || undefined);
      setAllConcerns(data);
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const handleResolve = async (id) => {
    try {
      await concernsApi.resolve(id, resolutionNotes);
      setMessage('Concern resolved.');
      setResolvingId(null);
      setResolutionNotes('');
      load();
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    }
  };

  const handleAcknowledge = async (id) => {
    try {
      await concernsApi.acknowledge(id);
      setMessage('Concern acknowledged.');
      load();
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    }
  };

  const handlePromoteToMR = async (concern) => {
    try {
      await mrApi.create({
        segment_id: concern.segment_id,
        concern_id: concern.id,
        description: `Promoted from concern: ${concern.description}`,
      });
      setMessage('Maintenance request created from concern.');
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    }
  };

  if (loading) return <div className="loading">Loading concerns...</div>;

  return (
    <div className="concerns-page">
      <h1>Concerns</h1>

      {message && (
        <div className={`alert ${message.startsWith('Error') ? 'alert-error' : 'alert-success'}`}>
          {message}<button onClick={() => setMessage(null)}>✕</button>
        </div>
      )}

      <div className="concern-filters">
        {['', 'open', 'acknowledged', 'resolved'].map((f) => (
          <button key={f} className={filter === f ? 'active' : ''} onClick={() => setFilter(f)}>
            {f || 'All'}
          </button>
        ))}
      </div>

      {allConcerns.length === 0 ? (
        <p className="empty-state">No concerns found.</p>
      ) : (
        <div className="concerns-list">
          {allConcerns.map((c) => (
            <div key={c.id} className={`concern-card concern-${c.status}`}>
              <div className="concern-card-header">
                <div>
                  <strong>{segmentName(c)}</strong>
                  <span className={`concern-status-badge status-${c.status}`}>{STATUS_LABELS[c.status]}</span>
                </div>
                <span className="concern-date">{new Date(c.created_at).toLocaleDateString()}</span>
              </div>
              <p className="concern-desc">{c.description}</p>
              <p className="concern-author">Reported by {c.raised_by_name}</p>

              {c.resolution_notes && (
                <p className="concern-resolution"><strong>Resolution:</strong> {c.resolution_notes}</p>
              )}

              {c.status !== 'resolved' && (
                <div className="concern-actions">
                  {c.status === 'open' && (
                    <button className="btn-sm btn-secondary" onClick={() => handleAcknowledge(c.id)}>Acknowledge</button>
                  )}
                  {resolvingId === c.id ? (
                    <div className="resolve-form">
                      <textarea placeholder="Resolution notes..." value={resolutionNotes}
                        onChange={(e) => setResolutionNotes(e.target.value)} rows={2} />
                      <div>
                        <button className="btn-sm btn-primary" onClick={() => handleResolve(c.id)}>Submit Resolution</button>
                        <button className="btn-sm btn-secondary" onClick={() => setResolvingId(null)}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <button className="btn-sm btn-primary" onClick={() => setResolvingId(c.id)}>Resolve</button>
                  )}
                  {(isAdmin || true) && ( /* any volunteer can promote */
                    <button className="btn-sm btn-secondary" onClick={() => handlePromoteToMR(c)}>
                      Create Maintenance Request
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
